import { ReactNode, useMemo } from 'react';
import { CssBaseline, StyledEngineProvider } from '@mui/material';
import { createTheme, ThemeOptions, ThemeProvider, TypographyVariantsOptions } from '@mui/material/styles';
import merge from 'lodash/merge';
import useConfig from 'hooks/useConfig';
import Palette from './palette';
import PaletteEfp from './paletteEfp';
import Typography from './typography';
import CustomShadows from './shadows';
import componentsOverride from './overrides';
import { CustomShadowProps } from 'types/theme';
import { PresetColor } from 'types/config';

type ThemeCustomizationProps = {
    children: ReactNode;
};

export default function ThemeCustomization({ children }: ThemeCustomizationProps) {
    const { themeDirection, mode, presetColor, fontFamily } = useConfig();

    const theme = useMemo(() => {
        if (presetColor === PresetColor.EFP) return PaletteEfp(mode);
        return Palette(mode, presetColor);
    }, [mode, presetColor]);

    const themeTypography = useMemo<TypographyVariantsOptions>(
        () => Typography(mode, fontFamily, theme),
        [mode, fontFamily, theme]
    );

    const themeCustomShadows = useMemo<CustomShadowProps>(() => CustomShadows(theme), [theme]);

    const themeOptions = useMemo<ThemeOptions>(
        () => ({
            breakpoints: {
                values: {
                    xs: 0,
                    sm: 768,
                    md: 1024,
                    lg: 1266,
                    xl: 1440,
                },
            },
            direction: themeDirection,
            mixins: {
                toolbar: {
                    minHeight: 60,
                    paddingTop: 8,
                    paddingBottom: 8,
                },
            },
            palette: theme.palette,
            customShadows: themeCustomShadows,
            typography: themeTypography,
        }),
        [themeDirection, theme, themeTypography, themeCustomShadows]
    );

    const themes = useMemo(() => {
        const newTheme = createTheme(themeOptions);
        newTheme.components = componentsOverride(newTheme);

        if (presetColor === PresetColor.EFP) {
            newTheme.components = merge(newTheme.components, theme.components);
        }

        return newTheme;
    }, [themeOptions, presetColor, theme]);

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={themes}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </StyledEngineProvider>
    );
}
